<template>
<ShopSubmenu active-tab="stuff" />
<div class="merch-detail-container">
    <div class="grid">
        <div class="left-column">
            <div class="flex-column">
                <div class="product-img focused">
                    <NuxtImg :src="getFeaturedImage" :alt="product?.title || ''" />
                </div>
                <div v-if="multipleImages" class="other-imgs-container">
                    <!--eslint-disable-next-line vuejs-accessibility/no-static-element-interactions-->
                    <div
                        v-for="(img) in product?.images"
                        :key="img.id"
                        class="product-img small"
                        :class="isImageSelected(img?.url)"
                        v-on:click="selectImage(img?.url)"
                        v-on:keydown.enter="selectImage(img?.url)"
                    >
                        <NuxtImg :src="img?.url" alt="" />
                    </div>
                </div>
            </div>
        </div>
        <div class="right-column">
            <h1 class="product-title">{{ product?.title || '' }}</h1>

            <div class="price-holder">
                <ShopPrice v-if="variant" :key="shopPriceKey" :product="variant" />
            </div>

            <div v-if="product && hasOptions" class="options">
                <ShopOptionSelector
                    v-for="(item) in product?.options"
                    :key="item.id"
                    :product-option="item"
                    v-on:change="handleOptionChange"
                />
            </div>
            <AddToCartButton :product="product" :variant="getVariantFromOptions()" />

            <!-- eslint-disable-next-line vue/no-v-html-->
            <div class="product-description-text" v-html="product?.descriptionHtml" />
        </div>
    </div>
</div>
</template>

<script setup lang="ts">
import type { Shopify } from '@jackboxgames/shopify'
import ShopSubmenu from '$components/shopping/ShopSubmenu.vue'
import ShopOptionSelector from '$components/shopping/ShopOptionSelector.vue'
import ShopPrice from '$components/shopping/ShopPrice.vue'
import AddToCartButton from '$components/shopping/AddToCartButton.vue'
import currencyCodeCloudfrontRegionMap from '$components/shopping/currencyCodeCloudfrontRegionMap.json'
import { currentCountry } from '$services/shopify'

const app = useNuxtApp()
const route = useRoute()
const i18n = useI18n()
const merchSlug = route.params.merchslug
const variant: Ref<Shopify.Variant | null> = ref(null)
const options: Map<string, string> = new Map()
const selectedImage = ref('')
const shopPriceKey = ref(0)

const { data: product } = await useAsyncData<Shopify.Product>(
    `merchdetail-${merchSlug}-${i18n.locale.value}`,
    async () => {
        try {
            if (!merchSlug) {
                throw new Error('Merch slug unavailable!')
            }
            const product = await app.$shopify.fetchProduct(merchSlug as string)
            return product
        } catch (error) {
            console.error(`Failed to get shopify product for '${merchSlug}':`, error.message)
            throw error
        }
    }
)
const getVariantFromOptions = (): Shopify.Variant | null => {
    if (!product?.value.variants) {
        return null
    }
    return product?.value.variants.find(
        (v) => v.selectedOptions?.every((o: Shopify.SelectedOption) => options.get(o.name) === o.value)
    ) || product?.value.variants[0] || null
}

const updateOptions = () => {
    variant.value = getVariantFromOptions()
    if (variant.value?.image) {
        selectedImage.value = variant.value?.image.url
    }
}

const getFeaturedImage = computed(() => {
    if (selectedImage.value) {
        return selectedImage.value
    }
    if (variant.value?.image) {
        return variant.value?.image.url
    }
    if (!product?.value.images?.length) return ''
    return product?.value.images[0].url
})

const multipleImages = computed(() => product?.value.images && product.value.images.length > 1)

const hasOptions = computed(() => (
    product?.value.options?.length
    && product?.value.options[0]?.values?.length
    && product?.value.options[0]?.values?.length > 1
))

const handleOptionChange = (option: string, value: string) => {
    options.set(option, value)
    updateOptions()
}

const isImageSelected = (url: string | undefined) => {
    if (selectedImage.value === url) return 'selected'
    return ''
}
const selectImage = (url: string | undefined) => {
    if (url && selectedImage.value !== url) {
        selectedImage.value = url
    }
}

const shouldReload = computed(() => {
    let res = false
    // check what currency is present from the fetched data and compare to geolocation cookie
    if (product.value.priceRange.maxVariantPrice.currencyCode) {
        const cc = product.value.priceRange.maxVariantPrice.currencyCode
        const doesMatch = currencyCodeCloudfrontRegionMap[cc].includes(currentCountry.code)
        if (!doesMatch) res = true
    }
    return res
})

watch(shouldReload, async () => {
    if (shouldReload.value === true) {
        await refreshNuxtData(`merchdetail-${merchSlug}-${i18n.locale.value}`)
        updateOptions()
        // increment component key to ensure currency updates
        shopPriceKey.value += 1
    }
}, { immediate: true })

onMounted(() => {
    updateOptions()
})

useHead({
    // TODO: get product title in here.
    title: `${i18n.t('NAVBAR.SHOP')}`
})

useJsonld(() => ({
    '@context': 'https://schema.org',
    '@type': 'ProductGroup',
    'name': product.value.title,
    'description': product.value.description,
    'image': product.value.images.map((image) => image.url),
    'offers': {
        '@context': 'https://schema.org',
        '@type': 'Offer',
        'priceCurrency': product.value.priceRange?.minVariantPrice?.currencyCode,
        'price': product.value.priceRange?.minVariantPrice?.amount,
        'itemCondition': 'https://schema.org/NewCondition',
        'availability': 'https://schema.org/InStock',
        'url': `https://jackboxgames.com/shop/merch/${product.value.handle}`
    },
    'hasVariant': product?.value.variants?.length > 1 ? product.value.variants.map((variant) => ({
        '@context': 'https://schema.org',
        '@type': 'Product',
        'image': variant.image.url,
        'name': `${product.value.title} - ${variant.title}`,
        'description': variant.title || variant.description,
        'size': variant.selectedOptions?.find((option) => option.name === 'Sizes')?.value,
        'color': variant.selectedOptions?.find((option) => option.name === 'Colors')?.value,
        'offers': {
            '@context': 'https://schema.org',
            '@type': 'Offer',
            'priceCurrency': variant.price?.currencyCode,
            'price': variant.price?.amount,
            'itemCondition': 'https://schema.org/NewCondition',
            'availability': 'https://schema.org/InStock',
            'url': `https://jackboxgames.com/shop/merch/${product.value.handle}`
        }
    })) : undefined
}))
</script>

<style lang="scss" scoped>
@use "$styles/kit.scss" as *;
.merch-detail-container {
    display: flex;
    justify-content: center;
    padding: 40px 0px;

    .cta-btn {
        margin-bottom: 32px;
    }

    .full-price {
        margin-top: 16px;
    }

    .help-text {
        font-size: 16px;
        font-weight: 500;
        line-height: 25px;
    }

    .product-description {
        font-size: 16px;
        font-weight: 400;
        line-height: 24px;
        margin-bottom: 24px;
    }

    .product-img {
        border-radius: 8px;

        &.focused {
            max-width: 550px;
            aspect-ratio: 1;
            width: auto;
            align-self: center;
        }

        &.small {
            :hover {
                scale: 105%;
            }
        }

        img {
            width: 100%;
            height: 100%;
            border-radius: 8px;
            object-fit: cover;
        }

        &.selected {
            outline: 4px solid var(--primary-200);
        }
    }


    .product-title {
        font-size: 28px;
        font-weight: 500;
        line-height: 32px;
        margin: 0px;
    }

    .other-imgs-container {
        display: flex;
        flex-wrap: wrap;
        gap: 10px;
        justify-content: center;
        margin-top: 24px;

        .product-img {
            height: 114px;
            width: 114px;
        }
    }

    .price-holder {
        margin: 20px 0 20px 0;

        :deep(.price) {
            flex-direction: row-reverse;
            justify-content: flex-end;
        }

        :deep(.item.price)  {
            font-size: 32px;
            line-height: 32px;
        }
    }
}

/* grid layout */
.grid {
    column-gap: 40px;
    grid-template-columns: repeat(10, 1fr);
    max-width: 1080px;

    @include mq-medium {
        margin-left: 40px;
        margin-right: 40px;
    }

    .left-column {
        grid-column-start: 1;
        grid-column-end:6;
    }

    .right-column {
        grid-column-start: 6;
        grid-column-end: 11;
    }

    @include mq-small-and-less {
        margin-left: 16px;
        margin-right: 16px;

        .left-column {
            grid-column-start: 1;
            grid-column-end: 11;
        }

        .right-column {
            margin-top: 20px;
            grid-column-start: 1;
            grid-column-end: 11;
        }
    }
}
</style>
